import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { emitEvent } from '@faceup/devtools'
import { sharedMessages, useIntlDeprecated } from '@faceup/localization'
import { useNotification } from '@faceup/ui-base'
import { buildUrl, DEFAULT_LANGUAGE, regionHeaderName } from '@faceup/utils'
import * as Sentry from '@sentry/react'
// @ts-expect-error Ignore types https://github.com/jaydenseric/apollo-upload-client/releases/tag/v18.0.0#:~:text=Implemented%20TypeScript%20types%20via%20JSDoc%20comments.
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { print } from 'graphql/language/printer'
import { createClient } from 'graphql-ws'
import { useMemo } from 'react'
import { possibleTypes } from './__generated__/possibleTypes.json'
import Auth from './utils/auth'
import { retrieveRegion } from './utils/useRegion'

const suppressedErrors: string[] = [
  'User has no permissions',
  'Report access was already given.',
  'Missing 2FA code',
  'Invalid login credentials',
]

const CustomApolloProvider = ({ children }: { children?: React.ReactNode }) => {
  const { formatMessage } = useIntlDeprecated()
  const notification = useNotification()
  // inspiration
  // > https://www.apollographql.com/docs/react/advanced/subscriptions.html
  // + file upload https://github.com/jaydenseric/apollo-upload-client#function-createuploadlink
  const httpLink = createUploadLink({
    uri: import.meta.env.VITE_API_URL ?? '',
    credentials: 'include',
    headers: { 'Apollo-Require-Preflight': 'true' },
  })

  const wsLink = new GraphQLWsLink(
    createClient({
      url: buildUrl(
        import.meta.env.VITE_WS_API_URL ?? '',
        retrieveRegion() ? { [regionHeaderName]: retrieveRegion() as string } : {}
      ),
      lazy: true,
      connectionParams: () => ({
        token: Auth.getJwt() || '',
        language: DEFAULT_LANGUAGE,
        origin: window.location.origin,
        ...(retrieveRegion() && { [regionHeaderName]: retrieveRegion() }),
      }),
      retryAttempts: Infinity,
    })
  )

  const errorLink = onError(({ operation, networkError, graphQLErrors }) => {
    if (suppressedErrors.includes(graphQLErrors?.[0]?.message ?? '')) {
      return
    }
    if (graphQLErrors?.some(error => error?.message === 'Outdated or corrupted JWT token')) {
      apolloClient.stop()
      apolloClient.clearStore()
      // using then, because onError is not promise friendly :(
      Auth.logout()
      window.location.replace('/')
    }

    if (graphQLErrors) {
      const errors = graphQLErrors.reduce(
        (acc, error) => ({
          ...acc,
          [(error as unknown as { name: string }).name]: error.message,
        }),
        {}
      )

      Sentry.captureException(`[GQL error]: ${operation.operationName}`, {
        extra: {
          query: print(operation.query),
          variables: operation.variables,
          ...errors,
        },
      })
    }

    const errorMessage = networkError?.message ?? graphQLErrors?.[0]?.message ?? ''
    console.error(errorMessage)
    notification.error({
      message: formatMessage(sharedMessages.apiError),
      description: errorMessage,
    })

    if (networkError) {
      Sentry.captureException(`[Network error]: ${operation.operationName}`, {
        extra: {
          query: print(operation.query),
          variables: operation.variables,
          message: networkError.message,
        },
      })
    }
  })

  const loggingLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        operation.setContext({ start: performance.now() })

        return forward(operation).map(response => {
          emitEvent('cache', operation.getContext()['cache']?.data?.data)

          emitEvent('requests', {
            name: operation.operationName,
            query: print(operation.query),
            variables: operation.variables,
            response: response as Record<string, unknown>,
            httpAnalyzer: {
              duration: performance.now() - operation.getContext()['start'],
            },
            isError: 'errors' in response,
            source: 'gql',
          })

          return response
        })
      }),
    []
  )

  const authLink = setContext((_, { headers }) => {
    const token = Auth.getJwt()

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        ...(retrieveRegion() && { [regionHeaderName]: retrieveRegion() }),
      },
    }
  })

  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query)

      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    loggingLink.concat(errorLink.concat(authLink.concat(httpLink)))
  )

  const apolloClient = new ApolloClient({
    link,
    cache: new InMemoryCache({
      possibleTypes,
    }),
  })

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

export default CustomApolloProvider
